import React, { useMemo, useState } from 'react';
import { Card, Row, Col, Typography, Modal, Form, Input, message, Space } from 'antd';
import StandardTable, { createActionColumn } from '../../components/common/StandardTable';
import type { ColumnType } from 'antd/es/table';
import { useGetMatClntWarehouses } from '../../hooks/genericHooks';
import type { MatWarehouses } from '../../types';
import { PlusOutlined, LeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { insertData, updateData } from '../../services/api';
import UserSelect from '../../components/modals/UserSelect';
import Button from '../../components/common/Button';

const MatClntWhouses: React.FC = () => {
  const { data = [], isLoading, refetch } = useGetMatClntWarehouses() as any;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MatWarehouses | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm<{ name: string; location?: string }>();
  const [owners, setOwners] = useState<string[]>([]);
  const [userModalOpen, setUserModalOpen] = useState(false);

  const unwrapRows = <T,>(payload: any): T[] => {
    if (Array.isArray(payload)) return payload as T[];
    const v1 = payload?.data;
    if (Array.isArray(v1)) return v1 as T[];
    const v2 = payload?.data?.data;
    if (Array.isArray(v2)) return v2 as T[];
    const v3 = payload?.data?.data?.data;
    if (Array.isArray(v3)) return v3 as T[];
    return [] as T[];
  };

  const columns: ColumnType<MatWarehouses>[] = useMemo(() => {
    const baseCols: ColumnType<MatWarehouses>[] = [
      { title: 'ID', dataIndex: 'id', key: 'id', width: 90 },
      { title: 'Name', dataIndex: 'name', key: 'name', width: 220 },
      { title: 'Location', dataIndex: 'location', key: 'location', width: 220 },
      { title: 'Owners', dataIndex: 'owners', key: 'owners', width: 220, render: (v: string[]) => Array.isArray(v) && v.length > 0 ? v.join(', ') : '-' },
      { title: 'Created By', dataIndex: 'created_by', key: 'created_by', width: 140, render: (v) => v || '-' },
      { title: 'Created At', dataIndex: 'created_at', key: 'created_at', width: 180, render: (v) => v ? new Date(v).toLocaleString() : '-' },
    ];
    const actionCol = createActionColumn<MatWarehouses>({
      title: 'Actions',
      // Client route is not yet defined; hide View to avoid broken navigation
      showView: false,
      onEdit: (record) => {
        setEditingRecord(record);
        setOwners(Array.isArray(record.owners) ? record.owners : []);
        setOpen(true);
      },
      showDelete: false,
      width: 100,
    });
    return [...baseCols, actionCol];
  }, []);

  const dataSource: MatWarehouses[] = unwrapRows<MatWarehouses>(data);

  return (
    <Card bordered={false} bodyStyle={{ padding: 8 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space align="center" size={8}>
            <Button icon={<LeftOutlined />} type="text" aria-label="Back" onClick={() => navigate(-1)} />
            <Typography.Title level={4} style={{ margin: 0 }}>
              Client Warehouses
            </Typography.Title>
          </Space>
        </Col>
        <Col>
          <Button
            action="add"
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingRecord(null);
              form.resetFields();
              setOwners([]);
              setOpen(true);
            }}
          >
            Create New
          </Button>
        </Col>
      </Row>
      <StandardTable<MatWarehouses>
        columns={columns}
        dataSource={dataSource}
        rowKey={(r) => String(r.id)}
        loading={isLoading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        enableExport
        scroll={{ x: 'max-content' }}
      />
      <Modal
        title={editingRecord ? 'Edit Client Warehouse' : 'Create Client Warehouse'}
        open={open}
        onCancel={() => { setOpen(false); setEditingRecord(null); }}
        zIndex={2000}
        getContainer={() => document.body}
        onOk={async () => {
          try {
            const values = await form.validateFields();
            setSubmitting(true);
            const nameUp = values.name?.trim().toUpperCase();
            const locUp = values.location?.trim().toUpperCase();
            if (editingRecord) {
              // Update existing record; do not send audit fields
              await updateData('mat_clnt_warehouses', editingRecord.id, {
                name: nameUp,
                location: locUp || null,
                owners: owners || [],
              });
              message.success('Client warehouse updated');
            } else {
              // Create new record
              await insertData('mat_clnt_warehouses', {
                name: nameUp,
                location: locUp || null,
                owners: owners || [],
              });
              message.success('Client warehouse created');
            }
            setOpen(false);
            form.resetFields();
            setEditingRecord(null);
            setOwners([]);
            if (typeof refetch === 'function') {
              await refetch();
            }
          } catch (err) {
            if ((err as any)?.errorFields) return; // validation error
            message.error('Failed to save client warehouse');
          } finally {
            setSubmitting(false);
          }
        }}
        confirmLoading={submitting}
        destroyOnClose
      >
        <Form
          key={editingRecord ? `edit-${editingRecord.id}` : 'create'}
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{
            name: editingRecord?.name || '',
            location: editingRecord?.location || '',
          }}
        >
          <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Please enter name' }]}> 
            <Input
              placeholder="Warehouse name"
              maxLength={255}
              onBlur={(e) => {
                const up = (e.target.value || '').toUpperCase();
                if (up !== e.target.value) form.setFieldsValue({ name: up });
              }}
            />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input
              placeholder="Location (optional)"
              maxLength={255}
              onBlur={(e) => {
                const up = (e.target.value || '').toUpperCase();
                if (up !== e.target.value) form.setFieldsValue({ location: up });
              }}
            />
          </Form.Item>
          <Form.Item label="Owners">
            <Space>
              <Button onClick={() => setUserModalOpen(true)}>Select Owners</Button>
              {owners && owners.length > 0 ? (
                <Typography.Text type="secondary">{owners.length} selected</Typography.Text>
              ) : (
                <Typography.Text type="secondary">No owners selected</Typography.Text>
              )}
            </Space>
          </Form.Item>
        </Form>
        <UserSelect
          open={userModalOpen}
          value={owners}
          title="Select Warehouse Owners"
          description="Choose one or more users to associate as owners of this client warehouse."
          onCancel={() => setUserModalOpen(false)}
          onOk={(list) => { setOwners(list); setUserModalOpen(false); }}
        />
      </Modal>
    </Card>
  );
};

export default MatClntWhouses;
